import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Sparkles, Save, Plus, X, Loader2, AlertTriangle, FileText, Send, CheckCircle2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, downloadPdf } from "@/api";
import { OFFICE, OfficeCard, OfficeButton, OfficeField, OfficeInput, OfficeTextarea } from "@/pages/admin/officeUi";
import { MONTHS_IT, eur } from "@/format";

export default function PricingStrategyPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const CUR = new Date().getFullYear();
  const [structure, setStructure] = useState(null);
  const [year, setYear] = useState(CUR + 1);
  const [units, setUnits] = useState([{ name: "", floor: "", ceiling: "" }]);
  const [premium, setPremium] = useState("10");
  const [instructions, setInstructions] = useState("");
  const [summary, setSummary] = useState("");
  const [rows, setRows] = useState([]);
  const [rules, setRules] = useState({});
  const [status, setStatus] = useState("draft");
  const [clientNote, setClientNote] = useState("");
  const [clientFeedback, setClientFeedback] = useState(null);
  const [sourceText, setSourceText] = useState("");
  const [salesFile, setSalesFile] = useState(null);
  const [oppFile, setOppFile] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [refineSummary, setRefineSummary] = useState("");
  const [busy, setBusy] = useState("");
  const fileRef = useRef(null);
  const oppFileRef = useRef(null);
  const actualsRef = useRef(null);

  useEffect(() => { api.getStructure(id).then(setStructure).catch(() => {}); }, [id]);

  const loadYear = useCallback(async (y) => {
    try {
      const d = await api.pricingGet(id, y);
      setRows((d.rows || []).map((r) => ({ ...r })));
      const flr = d.floors || {};
      const ceil = d.ceilings || {};
      setUnits((d.units && d.units.length ? d.units : [""]).map((n) => ({ name: n, floor: flr[n] != null ? String(flr[n]) : "", ceiling: ceil[n] != null ? String(ceil[n]) : "" })));
      if (d.weekend_premium != null) setPremium(String(Math.round(d.weekend_premium * 100)));
      setInstructions(d.instructions || "");
      setSummary(d.summary || "");
      setRules(d.rules || {});
      setStatus(d.status || "draft");
      setClientNote(d.client_note || "");
      setClientFeedback(d.client_feedback || null);
      setSourceText(d.source_text || "");
      setComparison(null); setRefineSummary("");
    } catch { /* noop */ }
  }, [id]);
  useEffect(() => { loadYear(year); }, [year, loadYear]);

  const setUnit = (i, k, v) => setUnits((u) => u.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const addUnit = () => setUnits((u) => [...u, { name: "", floor: "", ceiling: "" }]);
  const rmUnit = (i) => setUnits((u) => u.filter((_, idx) => idx !== i));
  const setCell = (idx, key, v) => setRows((rs) => rs.map((r, k) => (k === idx ? { ...r, [key]: v } : r)));
  const setRule = (k, v) => setRules((r) => ({ ...r, [k]: v }));

  const floorsMap = () => {
    const m = {};
    units.forEach((u) => { if (u.name.trim() && u.floor !== "" && u.floor != null) m[u.name.trim()] = Number(u.floor); });
    return m;
  };
  const ceilingsMap = () => {
    const m = {};
    units.forEach((u) => { if (u.name.trim() && u.ceiling !== "" && u.ceiling != null) m[u.name.trim()] = Number(u.ceiling); });
    return m;
  };

  const onFile = async () => {
    if (!salesFile) { toast.error("Carica prima il file vendite"); return; }
    const unitList = units.map((u) => u.name.trim()).filter(Boolean);
    setBusy("generate");
    try {
      const { job_id } = await api.pricingGenerate(id, year, unitList, floorsMap(), ceilingsMap(), (parseFloat(premium) || 0) / 100, instructions, salesFile, oppFile);
      let done = false;
      for (let i = 0; i < 45 && !done; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const st = await api.pricingJob(id, job_id);
        if (st.status === "done") {
          setRows((st.result.rows || []).map((r) => ({ ...r })));
          setSummary(st.result.summary || "");
          setRules(st.result.rules || {});
          setSourceText(st.result.source_text || "");
          if (st.result.units?.length) {
            const flr = st.result.floors || {};
            const ceil = st.result.ceilings || {};
            setUnits(st.result.units.map((n) => ({ name: n, floor: flr[n] != null ? String(flr[n]) : "", ceiling: ceil[n] != null ? String(ceil[n]) : "" })));
          }
          toast.success("Strategia generata! Controlla, modifica e salva.");
          done = true;
        } else if (st.status === "error") { toast.error("Generazione non riuscita, riprova"); done = true; }
      }
      if (!done) toast.error("La generazione sta impiegando troppo, riprova");
    } catch (err) { toast.error(err.message || "Errore"); }
    finally { setBusy(""); }
  };

  const save = async () => {
    setBusy("save");
    try {
      await doSave();
      toast.success("Strategia salvata");
    } catch (e) { toast.error(e.message || "Errore nel salvataggio"); }
    finally { setBusy(""); }
  };

  const doSave = async () => {
    await api.pricingSave(id, year, {
      units: units.map((u) => u.name.trim()).filter(Boolean),
      floors: floorsMap(),
      ceilings: ceilingsMap(),
      weekend_premium: (parseFloat(premium) || 0) / 100,
      rules,
      summary,
      instructions,
      rows: rows.map((r) => ({
        month: Number(r.month), unit: r.unit || "", band: r.band || "",
        bar_weekday: r.bar_weekday === "" || r.bar_weekday == null ? null : Number(r.bar_weekday),
        bar_weekend: r.bar_weekend === "" || r.bar_weekend == null ? null : Number(r.bar_weekend),
        min_stay: r.min_stay === "" || r.min_stay == null ? null : Number(r.min_stay),
        note: r.note || "",
      })),
      client_note: clientNote,
      source_text: sourceText,
    });
  };

  const onActuals = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy("actuals");
    try {
      const r = await api.pricingActualsImport(id, year, f);
      toast.success(`Vendite reali importate (${r.count} mesi)`);
      const c = await api.pricingComparison(id, year);
      setComparison(c);
    } catch (err) { toast.error(err.message || "Import non riuscito"); }
    finally { setBusy(""); }
  };

  const loadComparison = async () => {
    setBusy("compare");
    try { setComparison(await api.pricingComparison(id, year)); }
    catch (e) { toast.error(e.message || "Errore"); }
    finally { setBusy(""); }
  };

  const refine = async () => {
    setBusy("refine");
    try {
      const { rows: newRows, summary: rs } = await api.pricingRefine(id, year);
      setRows((rsOld) => {
        const next = [...rsOld];
        (newRows || []).forEach((nr) => {
          const idx = next.findIndex((r) => Number(r.month) === Number(nr.month) && r.unit === nr.unit);
          if (idx >= 0) next[idx] = { ...next[idx], ...nr };
          else next.push(nr);
        });
        return next;
      });
      setRefineSummary(rs || "");
      toast.success(`Strategia affinata (${(newRows || []).length} righe) — controlla e salva`);
    } catch (e) { toast.error(e.message || "Affinamento non riuscito"); }
    finally { setBusy(""); }
  };

  const publish = async (notify) => {
    setBusy("publish");
    try {
      await doSave();
      const r = await api.pricingPublish(id, year, { published: true, client_note: clientNote, notify_client: !!notify });
      setStatus("published");
      toast.success(notify ? `Strategia pubblicata · ${r.emails_sent || 0} email inviate` : "Strategia pubblicata al cliente");
    } catch (e) { toast.error(e.message || "Pubblicazione non riuscita"); }
    finally { setBusy(""); }
  };

  const unpublish = async () => {
    setBusy("publish");
    try {
      await api.pricingPublish(id, year, { published: false });
      setStatus("draft");
      toast.success("Strategia ritirata — non più visibile al cliente");
    } catch (e) { toast.error(e.message || "Operazione non riuscita"); }
    finally { setBusy(""); }
  };

  const downloadStrategyPdf = async () => {
    try {
      await downloadPdf(`/admin/structures/${id}/pricing/${year}/pdf`, `YieldUp-Strategia-${year}.pdf`);
    } catch { toast.error("Download del PDF non riuscito"); }
  };

  const regenMonth = async (month) => {
    setBusy(`regen-${month}`);
    try {
      const { rows: newRows } = await api.pricingRegenerate(id, year, { month });
      setRows((rs) => {
        const next = [...rs];
        (newRows || []).forEach((nr) => {
          const idx = next.findIndex((r) => Number(r.month) === Number(nr.month) && r.unit === nr.unit);
          if (idx >= 0) next[idx] = { ...next[idx], ...nr };
          else next.push(nr);
        });
        return next;
      });
      toast.success(`${MONTHS_IT[month - 1]} rigenerato — controlla e salva`);
    } catch (e) { toast.error(e.message || "Rigenerazione non riuscita"); }
    finally { setBusy(""); }
  };

  const regenRow = async (month, unit) => {
    setBusy(`regen-${month}-${unit}`);
    try {
      const { rows: newRows } = await api.pricingRegenerate(id, year, { month, unit });
      setRows((rs) => {
        const next = [...rs];
        (newRows || []).forEach((nr) => {
          const idx = next.findIndex((r) => Number(r.month) === Number(nr.month) && r.unit === nr.unit);
          if (idx >= 0) next[idx] = { ...next[idx], ...nr };
          else next.push(nr);
        });
        return next;
      });
      toast.success(`${unit} · ${MONTHS_IT[month - 1]} rigenerato — controlla e salva`);
    } catch (e) { toast.error(e.message || "Rigenerazione non riuscita"); }
    finally { setBusy(""); }
  };

  const floorFor = (unit) => { const m = floorsMap(); return m[unit]; };
  const byMonth = {};
  rows.forEach((r, idx) => { (byMonth[r.month] = byMonth[r.month] || []).push({ ...r, _idx: idx }); });

  return (
    <div style={{ background: OFFICE.surface2, minHeight: "100vh" }}>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <button onClick={() => nav(`/admin/structures/${id}`)} className="inline-flex items-center gap-2 text-sm mb-4" style={{ color: OFFICE.muted }} data-testid="pricing-back">
          <ArrowLeft className="h-4 w-4" /> Torna alla struttura
        </button>

        <OfficeCard>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: "linear-gradient(135deg,#1F9E73,#1F6FE0)" }}>
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="font-display text-xl" style={{ color: OFFICE.ink }}>Strategia Prezzi · {structure?.name || ""}</h1>
              <p className="text-xs" style={{ color: OFFICE.muted }}>Carica i dati dell'anno scorso, dai le tue indicazioni e l'AI crea la strategia tariffaria mese per mese</p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
            <OfficeField label="Anno strategia"><OfficeInput type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="pricing-year" /></OfficeField>
            <OfficeField label="Premio weekend (%)" hint="BAR weekend = feriale + questo premio"><OfficeInput value={premium} onChange={(e) => setPremium(e.target.value)} data-testid="pricing-premium" /></OfficeField>
          </div>

          <div className="mt-4">
            <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: OFFICE.muted }}>Unità / tipologie · prezzo minimo (pavimento) e massimo (tetto)</div>
            <div className="space-y-2">
              {units.map((u, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <div className="flex-1"><OfficeInput value={u.name} onChange={(e) => setUnit(i, "name", e.target.value)} placeholder="Es. Villa intera, Camera Deluxe, Suite…" data-testid={`pricing-unit-${i}`} /></div>
                  <div className="w-32"><OfficeInput value={u.floor} onChange={(e) => setUnit(i, "floor", e.target.value)} placeholder="Min € (pavimento)" data-testid={`pricing-floor-${i}`} /></div>
                  <div className="w-32"><OfficeInput value={u.ceiling} onChange={(e) => setUnit(i, "ceiling", e.target.value)} placeholder="Max € (tetto)" data-testid={`pricing-ceiling-${i}`} /></div>
                  {units.length > 1 ? <OfficeButton variant="ghost" onClick={() => rmUnit(i)}><X className="h-4 w-4" /></OfficeButton> : null}
                </div>
              ))}
            </div>
            <OfficeButton variant="ghost" className="mt-2" onClick={addUnit} data-testid="pricing-add-unit"><Plus className="h-4 w-4" /> Aggiungi unità</OfficeButton>
          </div>

          <div className="mt-4">
            <OfficeField label="Come vuoi impostare la strategia? (indicazioni per l'AI)" hint="Es. 'Spingi luglio-agosto, resta competitivo a gennaio, weekend più aggressivi'">
              <OfficeTextarea rows={3} value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="pricing-instructions" placeholder="Scrivi le tue preferenze…" />
            </OfficeField>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl p-3" style={{ background: OFFICE.surface3, border: `1px solid ${OFFICE.border}` }}>
              <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: OFFICE.muted }}>1 · File vendite (anno scorso)</div>
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv,.pdf" onChange={(e) => { setSalesFile(e.target.files?.[0] || null); e.target.value = ""; }} style={{ display: "none" }} />
              <OfficeButton variant="ghost" onClick={() => fileRef.current && fileRef.current.click()} data-testid="pricing-sales-file"><Upload className="h-4 w-4" /> {salesFile ? "Cambia file" : "Scegli file vendite"}</OfficeButton>
              {salesFile ? <div className="mt-2 text-xs flex items-center gap-1" style={{ color: OFFICE.ink }}><FileText className="h-3 w-3" /> {salesFile.name}</div> : null}
            </div>
            <div className="rounded-xl p-3" style={{ background: OFFICE.surface3, border: `1px solid ${OFFICE.border}` }}>
              <div className="text-xs font-mono uppercase tracking-widest mb-2" style={{ color: OFFICE.muted }}>2 · File opportunità (facoltativo)</div>
              <input ref={oppFileRef} type="file" accept=".xlsx,.xlsm,.csv,.pdf" onChange={(e) => { setOppFile(e.target.files?.[0] || null); e.target.value = ""; }} style={{ display: "none" }} />
              <OfficeButton variant="ghost" onClick={() => oppFileRef.current && oppFileRef.current.click()} data-testid="pricing-opp-file"><Upload className="h-4 w-4" /> {oppFile ? "Cambia file" : "Scegli file opportunità"}</OfficeButton>
              {oppFile ? <div className="mt-2 text-xs flex items-center gap-1" style={{ color: OFFICE.ink }}><FileText className="h-3 w-3" /> {oppFile.name}</div> : null}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <OfficeButton onClick={onFile} disabled={busy === "generate" || !salesFile} data-testid="pricing-generate-btn">
              {busy === "generate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {busy === "generate" ? "L'AI sta creando la strategia…" : "Genera strategia con AI"}
            </OfficeButton>
            {rows.length ? <OfficeButton variant="ghost" onClick={save} disabled={busy === "save"} data-testid="pricing-save-btn"><Save className="h-4 w-4" /> Salva bozza</OfficeButton> : null}
            {rows.length ? <OfficeButton variant="ghost" onClick={downloadStrategyPdf} data-testid="pricing-pdf-btn"><FileText className="h-4 w-4" /> Scarica piano giorno-per-giorno (PDF)</OfficeButton> : null}
          </div>
        </OfficeCard>

        {rows.length ? (
          <OfficeCard className="mt-6" data-testid="pricing-share">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-display text-base" style={{ color: OFFICE.ink }}>Condivisione col cliente</h3>
                <p className="text-xs mt-0.5" style={{ color: OFFICE.muted }}>
                  {status === "published"
                    ? "Pubblicata: il cliente vede la strategia nel suo portale e può confermarla o chiedere modifiche."
                    : "Bozza: la strategia non è ancora visibile al cliente."}
                </p>
              </div>
              <span className="rounded-full px-3 py-1 text-xs font-medium"
                style={status === "published" ? { background: "#E6F2EA", color: "#2E8B4F" } : { background: "#F5F3EF", color: OFFICE.muted }}
                data-testid="pricing-status-pill">
                {status === "published" ? "Pubblicata" : "Bozza"}
              </span>
            </div>

            {status !== "published" ? (
              <div className="mt-4 rounded-xl p-3 flex items-start gap-3" style={{ background: "#FBF1DC", border: "1px solid #E7CE9B" }} data-testid="pricing-draft-notice">
                <AlertTriangle className="h-5 w-5 shrink-0" style={{ color: "#B8841F" }} />
                <div className="text-sm" style={{ color: OFFICE.ink }}>
                  <b>Il cliente non vede ancora questa strategia.</b> Salvare la tiene come bozza interna. Per mostrarla nel portale del cliente premi <b>«Pubblica e avvisa il cliente»</b> qui sotto.
                </div>
              </div>
            ) : null}

            <div className="mt-4">
              <OfficeField label="Nota per il cliente (facoltativa)" hint="Appare in cima alla strategia che vede il cliente e nel PDF">
                <OfficeTextarea rows={3} value={clientNote} onChange={(e) => setClientNote(e.target.value)} data-testid="pricing-client-note" placeholder="Es. Ho impostato tariffe più alte in alta stagione; possiamo rivederle insieme." />
              </OfficeField>
            </div>

            {clientFeedback ? (
              <div className="mt-4 rounded-xl p-3" data-testid="pricing-feedback"
                style={{ background: clientFeedback.state === "confirmed" ? "#E6F2EA" : "#FBF1DC", border: `1px solid ${OFFICE.border}` }}>
                <div className="text-sm font-medium" style={{ color: OFFICE.ink }}>
                  {clientFeedback.state === "confirmed" ? "✓ Il cliente ha confermato la strategia" : "✎ Il cliente ha chiesto modifiche"}
                </div>
                {clientFeedback.message ? <div className="text-sm mt-1" style={{ color: OFFICE.ink2 }}>{clientFeedback.message}</div> : null}
                {clientFeedback.at ? <div className="text-xs mt-1" style={{ color: OFFICE.muted }}>{new Date(clientFeedback.at).toLocaleString("it-IT")}</div> : null}
              </div>
            ) : null}

            <div className="mt-4 flex flex-wrap gap-2">
              {status === "published" ? (
                <>
                  <OfficeButton onClick={() => publish(true)} disabled={busy === "publish"} data-testid="pricing-notify-btn">
                    {busy === "publish" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Aggiorna e avvisa via email
                  </OfficeButton>
                  <OfficeButton variant="ghost" onClick={() => publish(false)} disabled={busy === "publish"}><Save className="h-4 w-4" /> Aggiorna nota</OfficeButton>
                  <OfficeButton variant="danger" onClick={unpublish} disabled={busy === "publish"}><X className="h-4 w-4" /> Ritira dal cliente</OfficeButton>
                </>
              ) : (
                <>
                  <OfficeButton onClick={() => publish(true)} disabled={busy === "publish"} data-testid="pricing-publish-btn">
                    {busy === "publish" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Pubblica e avvisa il cliente
                  </OfficeButton>
                  <OfficeButton variant="ghost" onClick={() => publish(false)} disabled={busy === "publish"}><CheckCircle2 className="h-4 w-4" /> Pubblica senza email</OfficeButton>
                </>
              )}
            </div>
          </OfficeCard>
        ) : null}

        {summary || rows.length ? (
          <OfficeCard className="mt-6">
            <OfficeField label="Commento generale della strategia (modificabile)">
              <OfficeTextarea rows={5} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="pricing-summary" placeholder="Il commento generale dell'AI apparirà qui dopo la generazione…" />
            </OfficeField>
          </OfficeCard>
        ) : null}

        {rows.length ? (
          <OfficeCard className="mt-6" data-testid="pricing-rules">
            <h3 className="font-display text-base" style={{ color: OFFICE.ink }}>Regole operative</h3>
            <p className="text-xs mt-0.5" style={{ color: OFFICE.muted }}>Soggiorno minimo lo imposti per mese qui sotto. Queste regole valgono per tutto l'anno.</p>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
              <OfficeField label="Sconto notti-orfane %" hint="per riempire i buchi tra 2 prenotazioni">
                <OfficeInput value={rules.orphan_discount_pct ?? ""} onChange={(e) => setRule("orphan_discount_pct", e.target.value)} data-testid="rule-orphan" placeholder="es. 20" />
              </OfficeField>
              <OfficeField label="Early-bird: giorni">
                <OfficeInput value={rules.early_bird_days ?? ""} onChange={(e) => setRule("early_bird_days", e.target.value)} placeholder="es. 90" />
              </OfficeField>
              <OfficeField label="Early-bird: sconto %">
                <OfficeInput value={rules.early_bird_pct ?? ""} onChange={(e) => setRule("early_bird_pct", e.target.value)} placeholder="es. 10" />
              </OfficeField>
              <OfficeField label="Last-minute: giorni">
                <OfficeInput value={rules.last_minute_days ?? ""} onChange={(e) => setRule("last_minute_days", e.target.value)} placeholder="es. 7" />
              </OfficeField>
              <OfficeField label="Last-minute: sconto %">
                <OfficeInput value={rules.last_minute_pct ?? ""} onChange={(e) => setRule("last_minute_pct", e.target.value)} placeholder="es. 15" />
              </OfficeField>
            </div>
            <div className="mt-3">
              <OfficeField label="Nota sulle regole (facoltativa)">
                <OfficeTextarea rows={2} value={rules.note ?? ""} onChange={(e) => setRule("note", e.target.value)} data-testid="rule-note" placeholder="Es. applica il minimo 2 notti nei ponti e 1 notte per le orfane." />
              </OfficeField>
            </div>
          </OfficeCard>
        ) : null}

        {rows.length ? (
          <OfficeCard className="mt-6" data-testid="pricing-results">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-display text-base" style={{ color: OFFICE.ink }}>Risultati · strategia vs reale</h3>
                <p className="text-xs mt-0.5" style={{ color: OFFICE.muted }}>Importa le vendite reali, confronta col consigliato e lascia che l'AI affini la strategia.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <input ref={actualsRef} type="file" accept=".xlsx,.xlsm,.csv,.pdf" onChange={onActuals} style={{ display: "none" }} />
                <OfficeButton variant="ghost" onClick={() => actualsRef.current && actualsRef.current.click()} disabled={busy === "actuals"} data-testid="actuals-import-btn">
                  {busy === "actuals" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Importa vendite reali
                </OfficeButton>
                <OfficeButton variant="ghost" onClick={loadComparison} disabled={busy === "compare"} data-testid="compare-btn">Confronta</OfficeButton>
                <OfficeButton onClick={refine} disabled={busy === "refine"} data-testid="refine-btn">
                  {busy === "refine" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Affina con l'AI
                </OfficeButton>
              </div>
            </div>
            {refineSummary ? <div className="mt-3 rounded-xl p-3 text-sm" style={{ background: OFFICE.surface3, border: `1px solid ${OFFICE.border}`, color: OFFICE.ink2 }}>{refineSummary}</div> : null}
            {comparison ? (
              <div className="mt-4" data-testid="comparison-table">
                {comparison.estimated_uplift != null ? (
                  <div className="mb-3 rounded-xl p-3" style={{ background: "#E6F2EA", border: `1px solid ${OFFICE.border}` }}>
                    <span className="text-sm" style={{ color: OFFICE.ink }}>Guadagno stimato vs partenza: </span>
                    <span className="font-display text-lg" style={{ color: comparison.estimated_uplift >= 0 ? "#2E8B4F" : "#C0341A" }}>
                      {comparison.estimated_uplift >= 0 ? "+" : "−"}{eur(Math.abs(comparison.estimated_uplift))}
                    </span>
                  </div>
                ) : null}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" style={{ color: OFFICE.ink }}>
                    <thead>
                      <tr style={{ color: OFFICE.muted }}>
                        <th className="text-left font-mono text-xs uppercase py-1">Mese</th>
                        <th className="text-right font-mono text-xs uppercase py-1">ADR consigliato</th>
                        <th className="text-right font-mono text-xs uppercase py-1">ADR reale</th>
                        <th className="text-right font-mono text-xs uppercase py-1">Scostamento</th>
                        <th className="text-right font-mono text-xs uppercase py-1">Occ. reale</th>
                        <th className="text-right font-mono text-xs uppercase py-1">Fatturato reale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {comparison.months.map((m) => (
                        <tr key={m.month} style={{ borderTop: `1px solid ${OFFICE.border}` }}>
                          <td className="py-1.5">{MONTHS_IT[m.month - 1]}</td>
                          <td className="text-right">{m.rec_adr != null ? eur(m.rec_adr) : "—"}</td>
                          <td className="text-right">{m.actual_adr != null ? eur(m.actual_adr) : "—"}</td>
                          <td className="text-right" style={{ color: m.adr_gap_pct == null ? OFFICE.muted : m.adr_gap_pct >= 0 ? "#2E8B4F" : "#C0341A" }}>
                            {m.adr_gap_pct == null ? "—" : `${m.adr_gap_pct > 0 ? "+" : ""}${m.adr_gap_pct}%`}
                          </td>
                          <td className="text-right">{m.actual_occupancy != null ? `${m.actual_occupancy}%` : "—"}</td>
                          <td className="text-right">{m.actual_revenue != null ? eur(m.actual_revenue) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </OfficeCard>
        ) : null}

        {rows.length ? (
          <OfficeCard className="mt-6" data-testid="pricing-table">
            {MONTHS_IT.map((mName, mi) => {
              const list = byMonth[mi + 1] || [];
              if (!list.length) return null;
              return (
                <div key={mi} className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-display text-base" style={{ color: OFFICE.ink }}>{mName}</div>
                    <OfficeButton variant="ghost" className="!py-1.5 !px-3" onClick={() => regenMonth(mi + 1)}
                      disabled={busy === `regen-${mi + 1}`} data-testid={`pricing-regen-${mi + 1}`}>
                      {busy === `regen-${mi + 1}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Rigenera mese
                    </OfficeButton>
                  </div>
                  <div className="space-y-3">
                    {list.map((r) => {
                      const fl = floorFor(r.unit);
                      const ce = ceilingsMap()[r.unit];
                      const belowWd = fl != null && r.bar_weekday !== "" && r.bar_weekday != null && Number(r.bar_weekday) < fl;
                      const belowWk = fl != null && r.bar_weekend !== "" && r.bar_weekend != null && Number(r.bar_weekend) < fl;
                      const aboveWd = ce != null && r.bar_weekday != null && r.bar_weekday !== "" && Number(r.bar_weekday) > ce;
                      const aboveWk = ce != null && r.bar_weekend != null && r.bar_weekend !== "" && Number(r.bar_weekend) > ce;
                      const warn = belowWd || belowWk || aboveWd || aboveWk;
                      return (
                        <div key={r._idx} className="rounded-xl p-3" style={{ background: OFFICE.surface3, border: `1px solid ${OFFICE.border}` }}>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                            <div className="col-span-2 md:col-span-1">
                              <div className="flex items-center justify-between gap-2">
                                <div className="text-sm font-medium" style={{ color: OFFICE.ink }}>{r.unit}</div>
                                <button onClick={() => regenRow(mi + 1, r.unit)} disabled={busy === `regen-${mi + 1}-${r.unit}`}
                                  className="inline-flex items-center gap-1 text-xs disabled:opacity-50" style={{ color: OFFICE.brand }}
                                  data-testid={`pricing-regen-row-${r._idx}`} title="Rigenera con l'AI solo questa unità">
                                  {busy === `regen-${mi + 1}-${r.unit}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} Rigenera
                                </button>
                              </div>
                              <div className="mt-1"><OfficeInput value={r.band || ""} onChange={(e) => setCell(r._idx, "band", e.target.value)} placeholder="Fascia" /></div>
                            </div>
                            <OfficeField label={`Feriale €${fl != null ? ` · min ${fl}` : ""}${ce != null ? ` · max ${ce}` : ""}`}>
                              <OfficeInput value={r.bar_weekday ?? ""} onChange={(e) => setCell(r._idx, "bar_weekday", e.target.value)} data-testid={`pricing-weekday-${r._idx}`}
                                style={(belowWd || aboveWd) ? { borderColor: "#C0341A", color: "#C0341A" } : undefined} />
                            </OfficeField>
                            <OfficeField label={`Weekend €${fl != null ? ` · min ${fl}` : ""}${ce != null ? ` · max ${ce}` : ""}`}>
                              <OfficeInput value={r.bar_weekend ?? ""} onChange={(e) => setCell(r._idx, "bar_weekend", e.target.value)}
                                style={(belowWk || aboveWk) ? { borderColor: "#C0341A", color: "#C0341A" } : undefined} />
                            </OfficeField>
                            <OfficeField label="Soggiorno min (notti)">
                              <OfficeInput value={r.min_stay ?? ""} onChange={(e) => setCell(r._idx, "min_stay", e.target.value)} data-testid={`pricing-minstay-${r._idx}`} placeholder="es. 2" />
                            </OfficeField>
                            <div className="md:col-span-1 flex items-end h-full">
                              {warn ? <span className="text-xs flex items-center gap-1" style={{ color: "#C0341A" }}><AlertTriangle className="h-3 w-3" /> {(belowWd || belowWk) ? "Sotto il minimo" : "Oltre il tetto"}</span> : null}
                            </div>
                          </div>
                          <div className="mt-2">
                            <OfficeTextarea rows={2} value={r.note || ""} onChange={(e) => setCell(r._idx, "note", e.target.value)} placeholder="Nota / razionale (feste, ponti, storico…)" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            <div className="mt-2"><OfficeButton onClick={save} disabled={busy === "save"} data-testid="pricing-save-btn-2"><Save className="h-4 w-4" /> Salva bozza</OfficeButton></div>
          </OfficeCard>
        ) : null}
      </div>
    </div>
  );
}
